import { useMemo } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { useParams } from "react-router"
import { useProject } from "authoring/lib/hooks/use-project"
import { getDraftOrPublishedSchedules } from "authoring/routes/project/controller/get-draft-or-published-schedules"
import { refreshOnlyFor } from "lib/graphql/api"
import { withComparison } from "lib/graphql/comparison-function"
import { isSame } from "library/normalized-stringify/index.mjs"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { navigateProject, navigateSequentialReview } from "authoring/routes/project/utilities/project-navigation"
import { useNumericSearchParam } from "lib/@hooks/use-search-param"
import { successSnackbar } from "lib/snackbar/success-snackbar"

export function SequentialReviewProvider({ children }) {
    const { projectId, scheduleId } = useParams()
    const { project } = useProject({ id: projectId })
    const scheduleIds = project?.scopedScheduleIds ?? []

    const schedules = getDraftOrPublishedSchedules.useResults(
        { projectId, scheduleIds },
        refreshOnlyFor(projectId),
        withComparison((left, right) => isSame(left, right, { invalidChars: [] }))
    )

    const reviewQueue = useMemo(buildReviewQueue, [schedules])
    const currentIndex = reviewQueue.findIndex((s) => s.id === scheduleId)
    const totalToReview = reviewQueue.length
    const [approved] = useNumericSearchParam("approved", 0)
    const [rejected] = useNumericSearchParam("rejected", 0)

    return (
        <Bound
            isSequentialReview
            reviewQueue={reviewQueue}
            currentIndex={currentIndex}
            totalToReview={totalToReview}
            goToNext={goToNext}
            exitReview={exitReview}
            projectName={project?.name}
        >
            {children}
        </Bound>
    )

    function buildReviewQueue() {
        if (!Array.isArray(schedules)) return []
        return schedules
            .filter((s) => s?.isDraft && s?.status === DraftStatus.SUBMITTED)
            .sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""))
    }

    async function goToNext(actionType) {
        const nextApproved = approved + (actionType === "approved" ? 1 : 0)
        const nextRejected = rejected + (actionType === "rejected" ? 1 : 0)
        const nextSchedules = reviewQueue.filter((s) => s.id !== scheduleId)
        if (nextSchedules.length > 0) {
            const nextIndex = Math.max(0, currentIndex) < nextSchedules.length ? Math.max(0, currentIndex) : 0
            navigateSequentialReview({
                projectId,
                scheduleId: nextSchedules[nextIndex].id,
                approved: nextApproved,
                rejected: nextRejected,
            })
        } else {
            const total = nextApproved + nextRejected
            successSnackbar(`Review complete — ${total} schedule${total !== 1 ? "s" : ""} reviewed`)
            navigateProject(projectId)
        }
    }

    function exitReview() {
        navigateProject(projectId)
    }
}
